/**
 * extensions/dicom-rt/src/components/LongitudinalStudy/LongitudinalStudy.js
 *
 * v14 (F-1) — add volumeMode state for PSMA+ volume mode toggle.
 *
 * Changes from v13 (E-4):
 *   - `volumeMode` state added ('all' | 'psma-positive'), default 'all'.
 *   - `setVolumeMode` drilled into FindingSection and AnalysisSection.
 *   - All other state, filter helpers, and runSuvCompute are unchanged.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@material-ui/core/styles';
import cornerstone from 'cornerstone-core';
import cornerstoneTools from 'cornerstone-tools';
import _ from 'lodash';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import { RoundedButtonGroup } from '@platform/ui';
import { CriteriaSection } from './sections/CriteriaSection';
import { MeasurementSection } from './sections/MeasurementSection';
import { FindingSection } from './sections/FindingSection';
import { ResponseSection } from './sections/ResponseSection';
import { AnalysisSection } from './sections/AnalysisSection';
import { AllLesionsSection } from './sections/AllLesionsSection';
import theme from '../theme';
import module from '../../modules/dicom-measurement/src';
import getRecipClassifiedROIContours from '../../modules/dicom-measurement/src/assessments/getRecipClassifiedROIContours';
import computeAllRoiSuvAcrossTimepoints from '../../modules/dicom-measurement/src/suv/computeAllRoiSuvAcrossTimepoints';
import computePsmaPosVolumesFromBridge, {
  seedCalculatingPsmaPosVolumes,
} from '../../modules/dicom-measurement/src/assessments/computePsmaPosVolumesFromBridge';
import {
  clearAllSuv,
  subscribe as subscribeSuvBridge,
  isComputing as isSuvComputing,
} from '../../modules/dicom-measurement/src/suv/suvViewportBridge';
import { scheduleRecompute } from '../../modules/dicom-measurement/src/assessments/recomputeOnEdit';
import { applyDemoCaseROIOverride } from '../../modules/dicom-measurement/src/assessments/demoCaseROIOverride';

const {
  guideline,
  getTimepoints,
  getClassifiedROIContours,
  getMeasurementSource,
  getAssessmentSource,
  getEndpointSource,
  createCsvContent,
} = module;
const { BODY_PARTS, matchTrackingTypeFilter } = guideline;

function classifyROIs(
  timepoints,
  ROIContours,
  criteria,
  forceUpdate = true,
  patientId = null
) {
  let result;
  if (criteria?.guidelineId === 'RECIP10') {
    result = getRecipClassifiedROIContours(
      timepoints,
      ROIContours,
      criteria,
      forceUpdate
    );
  } else {
    result = getClassifiedROIContours(
      timepoints,
      ROIContours,
      criteria,
      forceUpdate
    );
  }
  return applyDemoCaseROIOverride(result, criteria, patientId);
}

const log = (...args) => {
  // eslint-disable-next-line no-console
  console.log('[SUV-LS]', ...args);
};

function refreshAllViewports() {
  try {
    const enabled = cornerstone.getEnabledElements?.() || [];
    for (const en of enabled) {
      if (en?.image) cornerstone.updateImage(en.element);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[SUV-LS] refreshAllViewports failed', e);
  }
}

export const LongitudinalStudy = ({
  imageSets,
  PatientID,
  SeriesInstanceUID,
}) => {
  const editModule = cornerstoneTools.getModule('rtstruct-edit');
  const structureSet = editModule.getters.structureSet(SeriesInstanceUID);
  const initialCriteria = editModule.getters.criteria();

  const [ROIContours, setROIContours] = useState(structureSet.ROIContours);
  const [sectionName, setSectionName] = useState('response');
  const [criteria, setCriteria] = useState(initialCriteria);
  const [assessments, setAssessments] = useState(
    editModule.getters.assessments()
  );
  const [plotType] = useState('measurement');
  const [plotFeature, setPlotFeature] = useState('byLesion');
  const [filteredTrackingType, setFilteredTrackingType] = useState(
    initialCriteria?.guidelineId === 'RECIP10' ? 'all-rois' : 'all'
  );
  const [filteredBodyPart, setFilteredBodyPart] = useState('all');
  const [filteredPsmaStatus, setFilteredPsmaStatus] = useState('all'); // kept for compat
  const [selectedLesionNumber, setSelectedLesionNumber] = useState(-1);
  const [measurementTechnique, setMeasurementTechnique] = useState(
    initialCriteria?.guidelineId === 'RECIP10' ? 'VOLUMETRIC' : 'BIDIMENSIONAL'
  );
  const [timepointIndex, setTimepointIndex] = useState(0);
  // F-1: shared metric selector for Findings + Analysis
  // 'all'           → display CT sumVolume (default, unchanged behaviour)
  // 'psma-positive' → display sumPsmaPosVolume (RECIP only)
  const [volumeMode, setVolumeMode] = useState('all');
  const [allLesionsTrackingType, setAllLesionsTrackingType] = useState('all');

  // G-B: stable ref to recompute args so the fingerprint useEffect never
  // closes over stale imageSets / criteria / SeriesInstanceUID / setROIContours.
  const doRecomputeRef = useRef(null);
  const fingerprintRef = useRef(null);
  const debounceRef = useRef(null);
  const psmaRecomputeInflightRef = useRef(false);

  const runSuvCompute = useCallback(
    async currentCriteria => {
      if (currentCriteria?.guidelineId !== 'RECIP10') {
        log('skip — not RECIP');
        clearAllSuv();
        refreshAllViewports();
        return;
      }
      const seeded = seedCalculatingPsmaPosVolumes({
        ROIContours: editModule.getters.structureSet(SeriesInstanceUID)
          .ROIContours,
        imageSets,
      });
      seeded.forEach(roi =>
        editModule.setters.updateROI(SeriesInstanceUID, roi)
      );
      setROIContours(seeded);

      log('compute starting — iterating all timepoints');
      try {
        await computeAllRoiSuvAcrossTimepoints({
          imageSets,
          getStructureSet: uid => editModule.getters.structureSet(uid),
        });
        log('compute done — deriving psmaPosVolumeByTimepoint from histograms');

        const latest = editModule.getters.structureSet(SeriesInstanceUID)
          .ROIContours;
        const { ROIContours: withPsmaVol } = computePsmaPosVolumesFromBridge({
          ROIContours: latest,
          imageSets,
          criteria: currentCriteria,
        });
        withPsmaVol.forEach(roi =>
          editModule.setters.updateROI(SeriesInstanceUID, roi)
        );
        setROIContours(withPsmaVol.map(r => ({ ...r })));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[SUV-LS] compute failed', err);
      }
      refreshAllViewports();
      log('compute + psmaPosVolume finished');
    },
    [imageSets, editModule, SeriesInstanceUID]
  );

  // Bug fix: subscribe to bridge so PSMA+ vol updates regardless of which
  // section the user is on when SUV compute finishes. runSuvCompute's one-shot
  // setROIContours can be lost under React batching / section-switch races.
  useEffect(() => {
    if (criteria?.guidelineId !== 'RECIP10') return;

    const trigger = () => {
      if (isSuvComputing()) return;
      if (psmaRecomputeInflightRef.current) return;
      const latest = editModule.getters.structureSet(SeriesInstanceUID)
        ?.ROIContours;
      if (!latest?.length) return;
      psmaRecomputeInflightRef.current = true;
      try {
        const { ROIContours: withPsmaVol } = computePsmaPosVolumesFromBridge({
          ROIContours: latest,
          imageSets,
          criteria,
        });
        const fresh = withPsmaVol.map(r => ({ ...r }));
        fresh.forEach(roi =>
          editModule.setters.updateROI(SeriesInstanceUID, roi)
        );
        setROIContours(fresh);
        log('bridge-driven PSMA+ vol refresh complete');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[SUV-LS] bridge-driven recompute failed', err);
      } finally {
        psmaRecomputeInflightRef.current = false;
      }
    };

    const unsubscribe = subscribeSuvBridge(trigger);
    trigger(); // catch bridge data that already exists before mount
    return unsubscribe;
  }, [
    criteria.guidelineId,
    imageSets,
    SeriesInstanceUID,
    editModule,
    criteria,
  ]);

  const onROIUpdate = (ROINumber, bodyPart, trackingType) => {
    const oldRoi = ROIContours.find(roi => roi.ROINumber === ROINumber);
    const index = ROIContours.findIndex(roi => roi.ROINumber === ROINumber);
    const newROIContour = _.cloneDeep(ROIContours[index]);
    newROIContour.bodyPart = bodyPart;
    newROIContour.trackingType = trackingType;
    editModule.setters.updateROI(SeriesInstanceUID, newROIContour);
    const _structureSet = editModule.getters.structureSet(SeriesInstanceUID);
    setROIContours([..._structureSet.ROIContours]);

    // G-B: if body part or tracking type changed on a RECIP case, re-query
    // thresholds + PSMA+ vol (no PET reload needed — histograms unchanged).
    if (criteria?.guidelineId === 'RECIP10') {
      const bodyPartChanged = bodyPart !== oldRoi?.bodyPart;
      const trackingTypeChanged = trackingType !== oldRoi?.trackingType;
      if (bodyPartChanged || trackingTypeChanged) {
        doRecomputeRef.current?.(null);
      }
    }
  };

  const timepoints = getTimepoints(imageSets, ROIContours);
  const selectedTimepoint = timepoints[Number(timepointIndex)] || timepoints[0];
  const selectedTpCtSeriesUID = selectedTimepoint?.SeriesInstanceUID;

  // G-B-FIX: keep doRecomputeRef current so fingerprint useEffect never has stale closures.
  // null affectedPairs = psma-only (body part / type change, no SUV recompute).
  doRecomputeRef.current = affectedPairs =>
    scheduleRecompute({
      affectedPairs,
      imageSets,
      criteria,
      structureSetUID: SeriesInstanceUID,
      setROIContours,
    });

  // G-B-FIX: Map-based fingerprint diff — emits only the changed (ROI, tp) pairs
  // so computeRoiSuvForPairs resamples voxels for those pairs only (not all N×T).
  // Key: "ROINumber@@ctSeriesUID" (@@  avoids collision since ctSeriesUID contains colons).
  useEffect(() => {
    if (criteria?.guidelineId !== 'RECIP10' || !ROIContours.length) return;
    const current = new Map();
    for (const tp of timepoints) {
      for (const lesion of tp.Lesions || []) {
        const key = `${lesion.ROINumber}@@${tp.SeriesInstanceUID}`;
        current.set(
          key,
          `${lesion.volume ?? 0}:${lesion.maxLongAxisValue ??
            0}:${lesion.maxShortAxisValue ?? 0}`
        );
      }
    }
    const prev = fingerprintRef.current;
    if (prev) {
      const changedPairs = [];
      for (const [key, val] of current) {
        if (prev.get(key) !== val) {
          const [ROINumberStr, ctSeriesUID] = key.split('@@');
          changedPairs.push({ ROINumber: Number(ROINumberStr), ctSeriesUID });
        }
      }
      if (changedPairs.length > 0) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          doRecomputeRef.current?.(changedPairs);
        }, 800);
      }
    }
    fingerprintRef.current = current;
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [ROIContours.length, criteria.guidelineId, timepoints]);

  const filterByBodyPart = roi => {
    switch (filteredBodyPart) {
      case 'all':
        return true;
      case 'bone':
        return roi.bodyPart === BODY_PARTS.BONE;
      case 'soft-tissue':
        return roi.bodyPart !== BODY_PARTS.BONE;
      case 'brain':
        return roi.bodyPart === BODY_PARTS.BRAIN;
      case 'lung':
        return roi.bodyPart === BODY_PARTS.LUNG;
      case 'liver':
        return roi.bodyPart === BODY_PARTS.LIVER;
      case 'lymph-node':
        return roi.bodyPart === BODY_PARTS.LYMPHNODE;
      case 'adrenal-gland':
        return roi.bodyPart === BODY_PARTS.ADRENALGLAND;
      case 'pleural-effusion':
        return roi.bodyPart === BODY_PARTS.PLEURALEFFUSION;
      default:
        return false;
    }
  };

  const filterByTrackingType = roi =>
    matchTrackingTypeFilter(
      roi,
      filteredTrackingType,
      criteria?.guidelineId,
      selectedLesionNumber
    );

  const getFilteredROIContours = rois =>
    rois.filter(filterByTrackingType).filter(filterByBodyPart);

  const getRoiUnionExcludingPsma = getFilteredROIContours;

  const onUpdateCriteriaClick = newCriteria => {
    log('onUpdateCriteriaClick — guideline:', newCriteria?.guidelineId);
    editModule.setters.criteria(newCriteria);
    const tpForClassify = getTimepoints(imageSets, ROIContours);
    const classifiedROIContours = classifyROIs(
      tpForClassify,
      ROIContours,
      newCriteria,
      true,
      PatientID
    );
    classifiedROIContours.forEach(ROIContour => {
      editModule.setters.updateROI(SeriesInstanceUID, ROIContour);
    });
    setROIContours(classifiedROIContours);
    setCriteria(newCriteria);

    const isRecipNow = newCriteria?.guidelineId === 'RECIP10';
    const wasRecip = criteria?.guidelineId === 'RECIP10';
    if (isRecipNow && measurementTechnique !== 'VOLUMETRIC')
      setMeasurementTechnique('VOLUMETRIC');
    if (!isRecipNow && wasRecip) setMeasurementTechnique('BIDIMENSIONAL');
    if (isRecipNow !== wasRecip) {
      setFilteredPsmaStatus('all');
      setFilteredTrackingType(isRecipNow ? 'all-rois' : 'all');
      setVolumeMode(isRecipNow ? 'psma-positive' : 'all');
      setAllLesionsTrackingType('all');
    }
    runSuvCompute(newCriteria);
    window.alert('new criteria is set successfully');
  };

  const onAssessmentsChange = (seriesInstanceUID, key, input, reason) => {
    const index = assessments.findIndex(
      a => a.SeriesInstanceUID === seriesInstanceUID
    );
    const newAssessment = _.cloneDeep(assessments[index]);
    newAssessment[key] = input;
    if (
      [
        'targetResponse',
        'nonTargetResponse',
        'newLesionResponse',
        'steroidsUse',
        'clincalStatus',
      ].includes(key)
    ) {
      newAssessment.overallResponse = '';
    }
    editModule.setters.assessment(seriesInstanceUID, newAssessment, reason);
    setAssessments([
      ...assessments.slice(0, index),
      newAssessment,
      ...assessments.slice(index + 1),
    ]);
  };

  const resetAssessments = () => {
    const newAssessments = _.cloneDeep(assessments);
    imageSets.forEach(series => {
      const assessment = newAssessments.find(
        a => a.SeriesInstanceUID === series.SeriesInstanceUID
      );
      assessment.targetResponse = '';
      assessment.nonTargetResponse = '';
      assessment.newLesionResponse = '';
      assessment.newLesionTargetResponse = '';
      assessment.newLesionNonTargetResponse = '';
      assessment.clinicalStatus = '';
      assessment.steroidsUse = '';
      assessment.overallResponse = '';
      editModule.setters.assessment(series.SeriesInstanceUID, assessment);
    });
    setAssessments(newAssessments);
  };

  const onExportReportClick = async () => {
    const tps = getTimepoints(imageSets, ROIContours);
    const measurementSource = getMeasurementSource(tps, ROIContours);
    const measurementContent = createCsvContent(measurementSource);
    const assessmentSource = getAssessmentSource(tps, ROIContours);
    const assessmentContent = createCsvContent(assessmentSource);
    const endpointSource = getEndpointSource(assessmentSource, criteria);
    const endpointContent = createCsvContent(endpointSource);
    const zip = new JSZip();
    zip.file(`measurements.csv`, measurementContent);
    zip.file(`assessments.csv`, assessmentContent);
    zip.file(`endpoints.csv`, endpointContent);
    zip
      .generateAsync({ type: 'blob' })
      .then(blob => saveAs(blob, `report_${PatientID}.zip`));
  };

  return (
    <ThemeProvider theme={theme}>
      <div style={{ margin: '0 0 10px 0' }}>
        <RoundedButtonGroup
          options={[
            { value: 'criteria', label: 'Criteria' },
            { value: 'measurement', label: 'Measurements' },
            { value: 'finding', label: 'Findings' },
            { value: 'analysis', label: 'Analysis' },
            { value: 'all-lesions', label: 'All Lesions' },
            { value: 'response', label: 'Responses' },
          ]}
          value={sectionName}
          onValueChanged={value => setSectionName(value)}
        />
      </div>
      <div style={{ height: '70vh', minHeight: '300px', maxHeight: '70vh' }}>
        <TabPanel value={sectionName} index={'criteria'}>
          <CriteriaSection
            imageSets={imageSets}
            ROIContours={ROIContours}
            criteria={criteria}
            onUpdateCriteriaClick={onUpdateCriteriaClick}
          />
        </TabPanel>
        <TabPanel value={sectionName} index={'measurement'}>
          <MeasurementSection
            imageSets={imageSets}
            ROIContours={ROIContours}
            criteria={criteria}
            onROIUpdate={onROIUpdate}
            getFilteredROIContours={getFilteredROIContours}
            filteredTrackingType={filteredTrackingType}
            setFilteredTrackingType={setFilteredTrackingType}
            filteredBodyPart={filteredBodyPart}
            setFilteredBodyPart={setFilteredBodyPart}
            filteredPsmaStatus={filteredPsmaStatus}
            setFilteredPsmaStatus={setFilteredPsmaStatus}
            selectedLesionNumber={selectedLesionNumber}
            setSelectedLesionNumber={setSelectedLesionNumber}
            timepointIndex={Number(timepointIndex)}
            setTimepointIndex={v => setTimepointIndex(Number(v))}
          />
        </TabPanel>
        <TabPanel value={sectionName} index={'finding'}>
          <FindingSection
            imageSets={imageSets}
            ROIContours={ROIContours}
            criteria={criteria}
            assessments={assessments}
            getFilteredROIContours={getFilteredROIContours}
            getRoiUnionExcludingPsma={getRoiUnionExcludingPsma}
            filteredTrackingType={filteredTrackingType}
            setFilteredTrackingType={setFilteredTrackingType}
            filteredBodyPart={filteredBodyPart}
            setFilteredBodyPart={setFilteredBodyPart}
            filteredPsmaStatus={filteredPsmaStatus}
            setFilteredPsmaStatus={setFilteredPsmaStatus}
            selectedLesionNumber={selectedLesionNumber}
            setSelectedLesionNumber={setSelectedLesionNumber}
            measurementTechnique={measurementTechnique}
            setMeasurementTechnique={setMeasurementTechnique}
            volumeMode={volumeMode}
            setVolumeMode={setVolumeMode}
          />
        </TabPanel>
        <TabPanel value={sectionName} index={'analysis'}>
          <AnalysisSection
            imageSets={imageSets}
            ROIContours={ROIContours}
            criteria={criteria}
            assessments={assessments}
            plotType={plotType}
            getFilteredROIContours={getFilteredROIContours}
            getRoiUnionExcludingPsma={getRoiUnionExcludingPsma}
            filteredTrackingType={filteredTrackingType}
            setFilteredTrackingType={setFilteredTrackingType}
            filteredBodyPart={filteredBodyPart}
            setFilteredBodyPart={setFilteredBodyPart}
            filteredPsmaStatus={filteredPsmaStatus}
            setFilteredPsmaStatus={setFilteredPsmaStatus}
            selectedLesionNumber={selectedLesionNumber}
            setSelectedLesionNumber={setSelectedLesionNumber}
            measurementTechnique={measurementTechnique}
            setMeasurementTechnique={setMeasurementTechnique}
            volumeMode={volumeMode}
            setVolumeMode={setVolumeMode}
          />
        </TabPanel>
        <TabPanel value={sectionName} index={'all-lesions'}>
          <AllLesionsSection
            imageSets={imageSets}
            ROIContours={ROIContours}
            criteria={criteria}
            plotFeature={plotFeature}
            setPlotFeature={setPlotFeature}
            getFilteredROIContours={getFilteredROIContours}
            filteredBodyPart={filteredBodyPart}
            setFilteredBodyPart={setFilteredBodyPart}
            measurementTechnique={measurementTechnique}
            setMeasurementTechnique={setMeasurementTechnique}
            allLesionsTrackingType={allLesionsTrackingType}
            setAllLesionsTrackingType={setAllLesionsTrackingType}
            volumeMode={volumeMode}
            setVolumeMode={setVolumeMode}
          />
        </TabPanel>
        <TabPanel value={sectionName} index={'response'}>
          <ResponseSection
            imageSets={imageSets}
            ROIContours={ROIContours}
            criteria={criteria}
            assessments={assessments}
            onAssessmentsChange={onAssessmentsChange}
            resetAssessments={resetAssessments}
            onExportReportClick={onExportReportClick}
          />
        </TabPanel>
      </div>
    </ThemeProvider>
  );
};
LongitudinalStudy.propTypes = {
  imageSets: PropTypes.arrayOf(
    PropTypes.shape({ SeriesInstanceUID: PropTypes.string })
  ),
  PatientID: PropTypes.string,
  SeriesInstanceUID: PropTypes.string,
};

const TabPanel = props => {
  const { children, value, index } = props;
  return value === index && children;
};
